import { Button } from "@/components/ui/button";
import { Share2, Download } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface ShareButtonsProps {
  imageUrl: string;
  hairstyleName: string;
  iconOnly?: boolean;
}

export const ShareButtons = ({ imageUrl, hairstyleName, iconOnly = false }: ShareButtonsProps) => {
  const { toast } = useToast();
  const appUrl = "https://31c8ade3-961c-4ff7-95b7-4b1c33db4180.lovableproject.com";

  const shareWithNativeAPI = async () => {
    try {
      // Convert image to blob for sharing
      let blob: Blob;
      if (imageUrl.startsWith('http')) {
        const response = await fetch(imageUrl);
        blob = await response.blob();
      } else {
        const response = await fetch(imageUrl);
        blob = await response.blob();
      }

      const file = new File([blob], `cut-previews-${hairstyleName}.jpg`, { type: 'image/jpeg' });

      if (navigator.share && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: `My new ${hairstyleName}!`,
          text: `Check out my new hairstyle! Try Cut Previews yourself: ${appUrl}`,
        });
        
        toast({
          title: "Shared Successfully!",
          description: "Thanks for spreading the word!",
        });
      } else {
        // Fallback to download
        downloadImage(blob);
      }
    } catch (error: any) {
      if (error.name !== 'AbortError') {
        console.error('Share error:', error);
        toast({
          title: "Couldn't share",
          description: "Downloading image instead...",
        });
        
        // Fallback to download
        try {
          let blob: Blob;
          if (imageUrl.startsWith('http')) {
            const response = await fetch(imageUrl);
            blob = await response.blob();
          } else {
            const response = await fetch(imageUrl);
            blob = await response.blob();
          }
          downloadImage(blob);
        } catch (err) {
          toast({
            title: "Error",
            description: "Please try again.",
            variant: "destructive",
          });
        }
      }
    }
  };

  const downloadImage = (blob: Blob) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `cut-previews-${hairstyleName.toLowerCase().replace(/\s+/g, '-')}.jpg`;
    document.body.appendChild(link);
    link.click();
    
    // Clean up immediately after click
    setTimeout(() => {
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }, 100);
    
    toast({
      title: "Image Downloaded!",
      description: "Now you can share it on Instagram, Facebook, or anywhere!",
    });
  };

  return (
    <div className={`flex gap-2 ${iconOnly ? '' : 'p-4'}`}>
      <Button
        onClick={shareWithNativeAPI}
        className={`${iconOnly ? '' : 'flex-1'} bg-gradient-to-r from-primary to-accent hover:opacity-90`}
        size={iconOnly ? "icon" : "sm"}
      >
        <Share2 className={iconOnly ? "h-4 w-4" : "mr-2 h-4 w-4"} />
        {!iconOnly && "Share"}
      </Button>
      <Button
        onClick={async () => {
          try {
            let blob: Blob;
            if (imageUrl.startsWith('http')) {
              const response = await fetch(imageUrl);
              blob = await response.blob();
            } else {
              const response = await fetch(imageUrl);
              blob = await response.blob();
            }
            downloadImage(blob);
          } catch (error) {
            toast({
              title: "Error",
              description: "Please try again.",
              variant: "destructive",
            });
          }
        }}
        variant="secondary"
        size={iconOnly ? "icon" : "sm"}
        className={iconOnly ? '' : 'flex-1'}
      >
        <Download className={iconOnly ? "h-4 w-4" : "mr-2 h-4 w-4"} />
        {!iconOnly && "Download"}
      </Button>
    </div>
  );
};
